import { SparklineChart } from "@solomon-labs/design/components/charts";
import type { SparklineChartProps } from "@solomon-labs/design/components/charts";
import { Text } from "@solomon-labs/design/components/ui";
import { motion, useReducedMotion } from "motion/react";
import React from "react";

import {
    apyHoverPoints,
    nearestApyPoint,
    readoutLeft
} from "./apyHoverGeometry";

interface ApyHistoryChartProps
    extends Pick<
        SparklineChartProps,
        | "points"
        | "xValues"
        | "replayKey"
        | "labels"
        | "formatValue"
        | "height"
        | "id"
        | "endDot"
        | "loading"
        | "revealSeconds"
    > {
    startLabel: string;
    endLabel: string;
}

const INSPECTION_OPACITY = 0.22;
const INSPECTION_TRANSITION = {
    duration: 0.12,
    ease: [0.4, 0, 0.2, 1] as const
};

/** Stats-only interaction layer; the design-package chart remains the renderer. */
export function ApyHistoryChart(
    props: ApyHistoryChartProps
): React.ReactElement {
    // Reset inspection when the range or observations change, without remounting
    // SparklineChart and interfering with its existing reveal/replay behavior.
    const { points, xValues, replayKey } = props;
    const [selection, setSelection] = React.useState<{
        index: number;
        points: number[];
        xValues: number[] | undefined;
        replayKey: string | number | undefined;
    } | null>(null);
    const {
        labels,
        formatValue,
        startLabel,
        endLabel,
        loading,
        height = 120
    } = props;
    const rootRef = React.useRef<HTMLDivElement>(null);
    const valueRef = React.useRef<HTMLSpanElement>(null);
    const dateRef = React.useRef<HTMLSpanElement>(null);
    const startRef = React.useRef<HTMLSpanElement>(null);
    const endRef = React.useRef<HTMLSpanElement>(null);
    const [sizes, setSizes] = React.useState({
        width: 0,
        value: 0,
        date: 0,
        start: 0,
        end: 0
    });
    const reducedMotion = useReducedMotion();
    React.useEffect(() => {
        if (loading) setSelection(null);
    }, [loading]);
    const transition = reducedMotion ? { duration: 0 } : INSPECTION_TRANSITION;
    const coords = React.useMemo(
        () => apyHoverPoints(points, xValues, sizes.width, height),
        [points, xValues, sizes.width, height]
    );
    const canInspect = !loading && points.length > 0 && Boolean(labels?.length);
    const index =
        canInspect &&
        selection?.points === points &&
        selection.xValues === xValues &&
        selection.replayKey === replayKey
            ? selection.index
            : null;
    const point = index === null ? undefined : coords[index];
    const active = point !== undefined;
    const value =
        index === null
            ? ""
            : (formatValue?.(points[index]) ?? String(points[index]));
    const date = index === null ? "" : (labels?.[index] ?? "");

    React.useLayoutEffect(() => {
        const measure = (): void => {
            const next = {
                width: rootRef.current?.clientWidth ?? 0,
                value: valueRef.current?.getBoundingClientRect().width ?? 0,
                date: dateRef.current?.getBoundingClientRect().width ?? 0,
                start: startRef.current?.getBoundingClientRect().width ?? 0,
                end: endRef.current?.getBoundingClientRect().width ?? 0
            };
            setSizes((prev) =>
                Object.keys(next).every(
                    (key) =>
                        next[key as keyof typeof next] ===
                        prev[key as keyof typeof prev]
                )
                    ? prev
                    : next
            );
        };
        measure();
        const observer = new ResizeObserver(measure);
        [rootRef, valueRef, dateRef, startRef, endRef].forEach((ref) => {
            if (ref.current) observer.observe(ref.current);
        });
        return (): void => observer.disconnect();
    }, [value, date, startLabel, endLabel]);

    const select = (next: number | null): void =>
        setSelection(
            next === null
                ? null
                : {
                      index: next,
                      points,
                      xValues,
                      replayKey
                  }
        );
    const inspect = (event: React.PointerEvent<HTMLDivElement>): void => {
        if (!canInspect) return;
        const rect = event.currentTarget.getBoundingClientRect();
        select(
            nearestApyPoint(
                coords,
                ((event.clientX - rect.left) * sizes.width) / rect.width
            )
        );
    };
    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (!canInspect) return;
        const current = index ?? points.length - 1;
        let next: number | null;
        switch (event.key) {
            case "ArrowLeft":
            case "ArrowDown":
                next = Math.max(0, current - 1);
                break;
            case "ArrowRight":
            case "ArrowUp":
                next = Math.min(points.length - 1, current + 1);
                break;
            case "Home":
                next = 0;
                break;
            case "End":
                next = points.length - 1;
                break;
            case "Escape":
                next = null;
                break;
            default:
                return;
        }
        event.preventDefault();
        select(next);
    };
    const dateLeft = readoutLeft(point?.x ?? 0, sizes.date, sizes.width);
    const contextOpacity = active ? INSPECTION_OPACITY : 1;

    return (
        <div
            ref={rootRef}
            className="relative w-full touch-pan-y focus-visible:outline focus-visible:outline-1 focus-visible:outline-foreground-muted"
            role={canInspect ? "slider" : undefined}
            aria-label={canInspect ? "APY History Observation" : undefined}
            aria-valuemin={canInspect ? 1 : undefined}
            aria-valuemax={canInspect ? points.length : undefined}
            aria-valuenow={
                canInspect ? (index ?? points.length - 1) + 1 : undefined
            }
            aria-valuetext={
                canInspect
                    ? `${value || (formatValue?.(points[points.length - 1]) ?? points[points.length - 1])}, ${date || labels?.[points.length - 1]}`
                    : undefined
            }
            tabIndex={canInspect ? 0 : undefined}
            onPointerMove={inspect}
            onPointerDown={inspect}
            onPointerLeave={(event) => {
                if (event.pointerType !== "touch") select(null);
            }}
            onPointerCancel={() => select(null)}
            onFocus={() => {
                if (canInspect && index === null) select(points.length - 1);
            }}
            onBlur={() => select(null)}
            onKeyDown={onKeyDown}
            data-inspecting={active}
        >
            {/* Reserve the readout row so hover never moves the chart. */}
            <motion.div
                className="relative h-8 pointer-events-none"
                initial={false}
                animate={{ opacity: active ? 1 : 0 }}
                transition={transition}
                aria-hidden
            >
                <Text
                    variant="number"
                    render={<span ref={valueRef} />}
                    className="absolute top-0 whitespace-nowrap max-w-full"
                    style={{
                        left: readoutLeft(
                            point?.x ?? 0,
                            sizes.value,
                            sizes.width
                        )
                    }}
                >
                    {value}
                </Text>
            </motion.div>
            <div className="relative">
                <motion.div
                    initial={false}
                    animate={{ opacity: contextOpacity }}
                    transition={transition}
                    data-chart-context
                >
                    <SparklineChart {...props} labels={undefined} />
                </motion.div>
                {point && (
                    <svg
                        width="100%"
                        height={height}
                        className="absolute inset-0 pointer-events-none overflow-visible"
                        aria-hidden
                    >
                        <line
                            x1={point.x}
                            x2={point.x}
                            y1={0}
                            y2={height}
                            stroke="rgb(var(--color-foreground-base))"
                            strokeWidth={0.5}
                            strokeDasharray="3 3"
                        />
                        <rect
                            data-selected-point
                            x={point.x - 3}
                            y={point.y - 3}
                            width={6}
                            height={6}
                            fill="rgb(var(--color-accent-base))"
                        />
                    </svg>
                )}
            </div>
            <Text
                variant="body"
                render={<div />}
                className="relative flex justify-between mt-2 min-h-5 text-foreground-base"
                aria-hidden
            >
                <motion.span
                    ref={startRef}
                    initial={false}
                    transition={transition}
                    animate={{
                        opacity:
                            active && dateLeft < sizes.start
                                ? 0
                                : contextOpacity
                    }}
                >
                    {loading ? "" : startLabel}
                </motion.span>
                <motion.span
                    ref={endRef}
                    initial={false}
                    transition={transition}
                    animate={{
                        opacity:
                            active &&
                            dateLeft + sizes.date > sizes.width - sizes.end
                                ? 0
                                : contextOpacity
                    }}
                >
                    {loading ? "" : endLabel}
                </motion.span>
                <motion.span
                    className="absolute inset-0 pointer-events-none"
                    initial={false}
                    animate={{ opacity: active ? 1 : 0 }}
                    transition={transition}
                >
                    <Text
                        variant="number"
                        render={<span ref={dateRef} />}
                        className="absolute top-0 whitespace-nowrap max-w-full"
                        style={{ left: dateLeft }}
                    >
                        {date}
                    </Text>
                </motion.span>
            </Text>
        </div>
    );
}
